"use client";

import {
  Badge,
  Box,
  Group,
  MantineTheme,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useMemo } from "react";
import { SelectedAccountType } from "../types";
import FeatureResourcePreviewAccordion from "./FeatureResourcePreviewAccordion";
import classes from "./AccountTypePreview.module.css";

const accountTypeCopy: Record<
  SelectedAccountType,
  { title: string; badge: string; description: string }
> = {
  Basic: {
    title: "Basic Account",
    badge: "Limited access",
    description: "Essential revision tools",
  },
  Trial: {
    title: "Free Trial",
    badge: "7 day access",
    description: "7 days free access to some of our resources and features.",
  },
  Member: {
    title: "Membership",
    badge: "Full access",
    description: "Unlimited access to our premium resources and features.",
  },
};

export default function AccountTypePreview({
  selectedAccountType,
}: {
  selectedAccountType: SelectedAccountType;
}) {
  const theme = useMantineTheme();
  const accentColor = useMemo(
    () => getColourForSelectedAccountType(selectedAccountType, theme),
    [selectedAccountType, theme]
  );
  const cardStyle = useMemo(
    () => ({ "--accent-color": accentColor } as React.CSSProperties),
    [accentColor]
  );

  const copy = accountTypeCopy[selectedAccountType];

  return (
    <Box className={classes.card} style={cardStyle}>
      <Group
        justify="space-between"
        className={classes.header}
        wrap="nowrap"
      >
        <Title order={4} className={classes.title}>
          {copy.title}
        </Title>
        <Badge className={classes.badge} radius="md" size="lg">
          {copy.badge}
        </Badge>
      </Group>
      <Group gap="xs" mt="xs" className={classes.infoRow}>
        <InfoOutlined className={classes.infoIcon} />
        <Text className={classes.infoText}>{copy.description}</Text>
      </Group>
      <FeatureResourcePreviewAccordion
        selectedAccountType={selectedAccountType}
      />
    </Box>
  );
}

export function getColourForSelectedAccountType(
  selectedAccountType: SelectedAccountType,
  theme: MantineTheme
) {
  return selectedAccountType === "Basic"
    ? theme.colors.dark[2]
    : selectedAccountType === "Trial"
    ? theme.colors.lightblue[5]
    : theme.colors.gold[5];
}
